import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CHALLENGES, DIFFICULTY_COLOR, CATEGORY_ICONS } from './challengeRegistry';
import { evaluateChallenge, computeOverallScore, scoreToGrade } from './components/Evaluator';
import ResultsDashboard from './components/ResultsDashboard';
import { useAgent } from './AgentContext';
import { useTheme } from './ThemeContext';
import { saveDraft, loadDraft, clearDraft, getRunsByAgent } from './agentAPI';

import C01 from './challenges/C01_ContactForm';
import C02 from './challenges/C02_SettingsForm';
import C03 from './challenges/C03_ExpenseForm';
import C04 from './challenges/C04_DataFind';
import C05 from './challenges/C05_DataCount';
import C06 from './challenges/C06_GridBulk';
import C07 from './challenges/C07_TabNavigate';
import C08 from './challenges/C08_AccordionModal';
import C09 from './challenges/C09_DeepNav';
import C10 from './challenges/C10_DragOrder';
import C11 from './challenges/C11_KanbanRearrange';
import C12 from './challenges/C12_DynamicForm';
import C13 from './challenges/C13_MultiStepCheckout';
import C14 from './challenges/C14_SearchCart';
import C15 from './challenges/C15_DataReasoning';
import C16 from './challenges/C16_CrossReference';
import C17 from './challenges/C17_TrapForm';
import C18 from './challenges/C18_LiveStatePause';
import C19 from './challenges/C19_UndoRecovery';
import C20 from './challenges/C20_PaginatedSearch';
import C21 from './challenges/C21_CanvasPaint';
import C22 from './challenges/C22_DragConnect';
import C23 from './challenges/C23_DragBinSort';

import './AgentBench.css';

const CHALLENGE_COMPONENTS = { C01, C02, C03, C04, C05, C06, C07, C08, C09, C10, C11, C12, C13, C14, C15, C16, C17, C18, C19, C20, C21, C22, C23 };

const MODE_LOBBY = 'lobby';
const MODE_RUNNING = 'running';
const MODE_RESULTS = 'results';

export default function AgentBench() {
  const { agent, logout } = useAgent();
  const { theme, toggle } = useTheme();
  const [mode, setMode] = useState(MODE_LOBBY);
  const [activeChallengeId, setActiveChallengeId] = useState(null);
  const [results, setResults] = useState({});
  const [timer, setTimer] = useState(0);
  const [running, setRunning] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState([]);
  const [challengeTimer, setChallengeTimer] = useState(0);
  const [evalStartTime, setEvalStartTime] = useState(null);
  const [draftData, setDraftData] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [agentRuns, setAgentRuns] = useState([]);
  const timerRef = useRef(null);
  const challengeTimerRef = useRef(null);
  const challengeStartRef = useRef(null);
  const timerValueRef = useRef(0);

  const categories = [...new Set(CHALLENGES.map(c => c.category))];
  const difficulties = ['Easy', 'Medium', 'Hard', 'Expert'];

  const filteredChallenges = CHALLENGES.filter(c => {
    const catOk = selectedCategories.length === 0 || selectedCategories.includes(c.category);
    const diffOk = selectedDifficulty.length === 0 || selectedDifficulty.includes(c.difficulty);
    return catOk && diffOk;
  });

  useEffect(() => {
    if (running) {
      timerRef.current = setInterval(() => setTimer(t => { timerValueRef.current = t + 1; return t + 1; }), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [running]);

  // Load draft on mount if agent is logged in
  useEffect(() => {
    if (!agent) { setDraftData(null); return; }
    setDraftLoading(true);
    loadDraft(agent.agentId)
      .then(d => { if (d && d.results && Object.keys(d.results).length > 0) setDraftData(d); })
      .catch(() => {})
      .finally(() => setDraftLoading(false));
    getRunsByAgent(agent.agentId).then(r => setAgentRuns(r || [])).catch(() => {});
  }, [agent]);

  // Per-challenge live timer — resets when activeChallengeId changes, stops when result exists
  useEffect(() => {
    clearInterval(challengeTimerRef.current);
    if (!activeChallengeId || !running) return;
    setChallengeTimer(0);
    challengeTimerRef.current = setInterval(() => setChallengeTimer(t => t + 1), 1000);
    return () => clearInterval(challengeTimerRef.current);
  }, [activeChallengeId, running]); // eslint-disable-line

  // Freeze challenge timer once result is recorded
  useEffect(() => {
    if (activeChallengeId && results[activeChallengeId]) {
      clearInterval(challengeTimerRef.current);
    }
  }, [results, activeChallengeId]);

  const startEval = () => {
    setResults({});
    setTimer(0);
    setChallengeTimer(0);
    setRunning(true);
    setEvalStartTime(new Date().toISOString());
    setActiveChallengeId(filteredChallenges[0]?.id || null);
    setMode(MODE_RUNNING);
    challengeStartRef.current = Date.now();
  };

  // Save draft to D1 after state update
  const persistDraft = useCallback((newResults, activeId) => {
    if (!agent) return;
    saveDraft({
      agentId: agent.agentId,
      startedAt: evalStartTime,
      elapsed: timerValueRef.current,
      results: newResults,
      filters: { categories: selectedCategories, difficulty: selectedDifficulty },
      activeId,
    }).catch(() => {});
  }, [agent, evalStartTime, selectedCategories, selectedDifficulty]);

  const handleChallengeComplete = (rawResult) => {
    const evaluation = evaluateChallenge(activeChallengeId, rawResult);
    const timeSpent = Math.round((Date.now() - challengeStartRef.current) / 1000);
    const interactionCount = rawResult.interactionCount ?? null;
    const errorAttempts = rawResult.errorAttempts ?? null;
    setResults(prev => {
      const next = { ...prev, [activeChallengeId]: { ...evaluation, timeSpent, interactionCount, errorAttempts } };
      persistDraft(next, activeChallengeId);
      return next;
    });
  };

  const navigateToChallenge = (id) => {
    setActiveChallengeId(id);
    challengeStartRef.current = Date.now();
  };

  const finishEval = () => {
    setRunning(false);
    setMode(MODE_RESULTS);
    if (agent) clearDraft(agent.agentId).catch(() => {});
    setDraftData(null);
  };

  const resetEval = () => {
    setResults({});
    setTimer(0);
    timerValueRef.current = 0;
    setChallengeTimer(0);
    clearInterval(challengeTimerRef.current);
    setRunning(false);
    setMode(MODE_LOBBY);
    setActiveChallengeId(null);
    if (agent) clearDraft(agent.agentId).catch(() => {});
    setDraftData(null);
  };

  const pauseEval = () => {
    persistDraft(results, activeChallengeId);
    setRunning(false);
    setMode(MODE_LOBBY);
  };

  const resumeEval = (draft) => {
    const d = draft || draftData;
    if (!d) return;
    setResults(d.results || {});
    setTimer(d.elapsed || 0);
    timerValueRef.current = d.elapsed || 0;
    setEvalStartTime(d.startedAt);
    if (d.filters) {
      setSelectedCategories(d.filters.categories || []);
      setSelectedDifficulty(d.filters.difficulty || []);
    }
    const resumeId = d.activeId || filteredChallenges.find(c => !d.results?.[c.id])?.id || filteredChallenges[0]?.id;
    setActiveChallengeId(resumeId);
    setRunning(true);
    setMode(MODE_RUNNING);
    challengeStartRef.current = Date.now();
    setDraftData(null);
  };

  const discardDraft = () => {
    if (agent) clearDraft(agent.agentId).catch(() => {});
    setDraftData(null);
  };

  const retryChallenge = (id) => {
    setResults(prev => { const n = { ...prev }; delete n[id]; return n; });
    setActiveChallengeId(id);
    setMode(MODE_RUNNING);
    setRunning(true);
    challengeStartRef.current = Date.now();
  };

  const activeChallenge = CHALLENGES.find(c => c.id === activeChallengeId);
  const ChallengeComponent = activeChallengeId ? CHALLENGE_COMPONENTS[activeChallengeId] : null;
  const currentIdx = filteredChallenges.findIndex(c => c.id === activeChallengeId);
  const nextChallenge = filteredChallenges[currentIdx + 1];
  const prevChallenge = filteredChallenges[currentIdx - 1];
  const overall = computeOverallScore(Object.values(results), CHALLENGES);

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const formatChTime = (s) => s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
  const toggleFilter = (arr, setArr, val) => setArr(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);

  const completedIds = Object.keys(results);
  const allDone = filteredChallenges.every(c => completedIds.includes(c.id));

  return (
    <div className="ab-wrapper">
      {/* Top Bar */}
      <div className="ab-topbar">
        <div className="ab-topbar-left">
          <Link to="/arena" style={{ display:'flex', alignItems:'center', gap:8, textDecoration:'none' }}>
            <span className="ab-logo">🧪</span>
            <div>
              <div className="ab-topbar-title">AgentBench</div>
              <div className="ab-topbar-sub">CUA EVALS</div>
            </div>
          </Link>
        </div>
        {mode === MODE_RUNNING && (
          <div className="ab-topbar-center">
            <div className="ab-timer">⏱ {formatTime(timer)}</div>
            <div className="ab-progress-summary">
              {completedIds.length}/{filteredChallenges.length} completed ·
              <span className="ab-live-score"> {overall.pct ?? 0}% score</span>
            </div>
          </div>
        )}
        <div className="ab-topbar-right">
          {mode === MODE_RUNNING && (
            <>
              <button className="ab-topbar-btn ab-topbar-btn-pause" onClick={pauseEval}>
                ⏸ Pause
              </button>
              <button className="ab-topbar-btn" onClick={finishEval}>
                📊 {allDone ? 'View Results' : 'Finish & Score'}
              </button>
            </>
          )}
          {agent
            ? <>
                <Link to="/arena/my-runs" className="ab-topbar-nav">📋 My Runs</Link>
                <span className="ab-arena-agent-pill">🤖 {agent.name}</span>
                <button className="ab-topbar-nav ab-topbar-signout" onClick={logout}>Sign out</button>
              </>
            : <>
                <Link to="/arena/auth" className="ab-topbar-nav">🔑 Login</Link>
                <Link to="/arena/auth?tab=register" className="ab-topbar-nav-btn">Register</Link>
              </>
          }
          <Link to="/arena/leaderboard" className="ab-topbar-nav">🏆 Leaderboard</Link>
          <Link to="/arena" className="ab-topbar-nav">🏠 Arena</Link>
          <button className="ab-theme-toggle" onClick={toggle}>{theme === 'dark' ? '☀️' : '🌙'}</button>
        </div>
      </div>

      {/* LOBBY */}
      {mode === MODE_LOBBY && (
        <div className="ab-lobby">
          {/* Resume banner — shown when a saved draft exists */}
          {draftData && (
            <div className="ab-resume-banner">
              <div className="ab-resume-banner-icon">⏸</div>
              <div className="ab-resume-banner-info">
                <div className="ab-resume-banner-title">Paused Evaluation</div>
                <div className="ab-resume-banner-meta">
                  {Object.keys(draftData.results).length} challenges completed · {Math.floor((draftData.elapsed || 0) / 60)}m {(draftData.elapsed || 0) % 60}s elapsed
                  {draftData.updatedAt && <> · saved {new Date(draftData.updatedAt).toLocaleDateString()}</>}
                </div>
              </div>
              <button className="ab-resume-btn" onClick={() => resumeEval()}>▶ Resume</button>
              <button className="ab-discard-btn" onClick={discardDraft}>✕ Discard</button>
            </div>
          )}
          {draftLoading && (
            <div className="ab-resume-banner ab-resume-banner-loading">
              <span>Loading saved progress...</span>
            </div>
          )}
          {/* Agent context card — shown when logged in and has prior runs */}
          {agent && agentRuns.length > 0 && (() => {
            const best = agentRuns.reduce((b, r) => ((r.smartnessScore ?? 0) > (b?.smartnessScore ?? -1) ? r : b), null);
            const last = agentRuns[0];
            return (
              <div className="ab-agent-ctx">
                <div className="ab-agent-ctx-left">
                  <span className="ab-agent-ctx-label">Your best run</span>
                  <div className="ab-agent-ctx-stats">
                    <span className="ab-agent-ctx-stat"><strong>{best?.scorePct ?? 0}%</strong> accuracy</span>
                    <span className="ab-agent-ctx-dot">·</span>
                    <span className="ab-agent-ctx-stat"><strong>{best?.smartnessScore ?? 0}%</strong> smartness</span>
                    <span className="ab-agent-ctx-dot">·</span>
                    <span className="ab-agent-ctx-stat"><strong>{agentRuns.length}</strong> run{agentRuns.length !== 1 ? 's' : ''} total</span>
                  </div>
                </div>
                <Link to="/arena/my-runs" className="ab-agent-ctx-link">View All Runs →</Link>
              </div>
            );
          })()}
          <div className="ab-lobby-strip">
            <div className="ab-lobby-strip-left">
              <h1 className="ab-lobby-strip-title">🧪 AgentBench</h1>
              <span className="ab-lobby-strip-sub">CUA Evaluation Suite</span>
            </div>
            <div className="ab-lobby-strip-kpis">
              <span className="ab-lobby-strip-kpi"><strong>{CHALLENGES.length}</strong> challenges</span>
              <span className="ab-lobby-strip-kpi"><strong>{CHALLENGES.reduce((s, c) => s + c.points, 0)}</strong> pts</span>
              <span className="ab-lobby-strip-kpi"><strong>{categories.length}</strong> categories</span>
              <span className="ab-lobby-strip-kpi"><strong>{CHALLENGES.filter(c => c.difficulty === 'Expert').length}</strong> expert</span>
            </div>
          </div>

          <div className="ab-lobby-filters">
            <div className="ab-filter-group">
              <span className="ab-filter-label">Category</span>
              {categories.map(cat => (
                <button key={cat}
                  className={`ab-filter-chip ${selectedCategories.includes(cat) ? 'ab-filter-chip-active' : ''}`}
                  onClick={() => toggleFilter(selectedCategories, setSelectedCategories, cat)}>
                  <span className="ab-filter-chip-icon">{CATEGORY_ICONS[cat]}</span> {cat}
                </button>
              ))}
            </div>
            <div className="ab-filter-group">
              <span className="ab-filter-label">Difficulty</span>
              {difficulties.map(d => (
                <button key={d}
                  className={`ab-filter-chip ab-filter-chip-diff ${selectedDifficulty.includes(d) ? 'ab-filter-chip-active' : ''}`}
                  style={selectedDifficulty.includes(d) ? { borderColor: DIFFICULTY_COLOR[d], color: DIFFICULTY_COLOR[d], background: DIFFICULTY_COLOR[d] + '14' } : {}}
                  onClick={() => toggleFilter(selectedDifficulty, setSelectedDifficulty, d)}>
                  <span className="ab-diff-dot" style={{ background: DIFFICULTY_COLOR[d] }} />
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="ab-lobby-grid-wrap">
            <div className="ab-lobby-grid">
              {filteredChallenges.map((ch) => (
                <div key={ch.id} className={`ab-ch-card ab-ch-card--${ch.difficulty.toLowerCase()}`}>
                  <div className="ab-ch-card-accent" style={{ background: DIFFICULTY_COLOR[ch.difficulty] }} />
                  <div className="ab-ch-card-body">
                    <div className="ab-ch-card-top">
                      <span className="ab-ch-card-icon">{CATEGORY_ICONS[ch.category]}</span>
                      <span className="ab-ch-card-id">{ch.id}</span>
                      <div className="ab-ch-card-badges">
                        <span className="ab-ch-card-pts">{ch.points}pts</span>
                        <span className="ab-ch-card-diff" style={{ color: DIFFICULTY_COLOR[ch.difficulty] }}>{ch.difficulty}</span>
                      </div>
                    </div>
                    <div className="ab-ch-card-title">{ch.title}</div>
                    <div className="ab-ch-card-cat">{CATEGORY_ICONS[ch.category]} {ch.category}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="ab-lobby-footer">
            <div className="ab-lobby-footer-info">
              <span className="ab-lobby-footer-count">{filteredChallenges.length} challenges selected</span>
              <span className="ab-lobby-footer-pts">{filteredChallenges.reduce((s, c) => s + c.points, 0)} pts</span>
            </div>
            <button className="ab-start-btn" onClick={startEval} disabled={filteredChallenges.length === 0}>
              🚀 Start Evaluation
            </button>
          </div>
        </div>
      )}

      {/* RUNNING — 3-pane fixed layout */}
      {mode === MODE_RUNNING && activeChallenge && (
        <div className="ab-runner-layout">

          {/* Pane 1: Challenge list sidebar */}
          <div className="ab-runner-sidebar">
            <div className="ab-sidebar-title">Challenges</div>
            <div className="ab-sidebar-list">
              {filteredChallenges.map((ch, i) => {
                const r = results[ch.id];
                const isActive = ch.id === activeChallengeId;
                const g = r ? scoreToGrade(r.score) : null;
                return (
                  <button key={ch.id}
                    className={`ab-sidebar-ch ${isActive ? 'ab-sidebar-ch-active' : ''}`}
                    onClick={() => navigateToChallenge(ch.id)}>
                    <span className="ab-sidebar-ch-num">{i + 1}</span>
                    <span className="ab-sidebar-ch-id">{ch.id}</span>
                    {r
                      ? <span className="ab-sidebar-ch-score" style={{ color: g.color, background: g.bg }}>{r.score}%</span>
                      : <span className="ab-sidebar-ch-pending">—</span>
                    }
                  </button>
                );
              })}
            </div>
            <button className="ab-finish-btn" onClick={finishEval}>
              📊 {allDone ? 'Results' : 'Finish'}
            </button>
          </div>

          {/* Pane 2: Task brief (fixed, always visible) */}
          <div className="ab-task-pane">
            <div className="ab-task-pane-header">
              <div className="ab-task-id-badge">{activeChallenge.id}</div>
              <div className="ab-task-pane-meta">
                <span className="ab-diff-chip" style={{ color: DIFFICULTY_COLOR[activeChallenge.difficulty], borderColor: DIFFICULTY_COLOR[activeChallenge.difficulty] }}>
                  {activeChallenge.difficulty}
                </span>
                <span className="ab-pts-chip">{activeChallenge.points} pts</span>
                {results[activeChallengeId]
                ? <span className="ab-time-chip ab-time-chip-done">⏱ {formatChTime(results[activeChallengeId].timeSpent)}</span>
                : <span className="ab-time-chip ab-time-chip-live">⏱ {formatChTime(challengeTimer)}</span>
              }
              </div>
            </div>
            <div className="ab-task-pane-title">{activeChallenge.title}</div>
            <div className="ab-task-pane-cat">{CATEGORY_ICONS[activeChallenge.category]} {activeChallenge.category}</div>

            <div className="ab-task-pane-instructions">
              {activeChallenge.description.split('\n').filter(l => l.trim()).map((line, i) => (
                <div key={i} className={line.startsWith('•') ? 'ab-task-bullet' : 'ab-task-line'}>
                  {line}
                </div>
              ))}
            </div>

            {results[activeChallengeId] && (
              <div className="ab-task-pane-result">
                <div className="ab-result-grade-pill"
                  style={{ color: scoreToGrade(results[activeChallengeId].score).color, background: scoreToGrade(results[activeChallengeId].score).bg }}>
                  {results[activeChallengeId].score}% — {scoreToGrade(results[activeChallengeId].score).label}
                </div>
                <div className="ab-task-pane-feedback">
                  {results[activeChallengeId].feedback.map((fb, i) => (
                    <div key={i} className={`ab-fb ab-fb-${fb.type}`}>{fb.msg}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="ab-task-pane-nav">
              <button className="ab-nav-btn" onClick={() => prevChallenge && navigateToChallenge(prevChallenge.id)} disabled={!prevChallenge}>← Prev</button>
              <span className="ab-nav-pos">{currentIdx + 1}/{filteredChallenges.length}</span>
              {nextChallenge
                ? <button className="ab-nav-btn ab-nav-btn-next" onClick={() => navigateToChallenge(nextChallenge.id)}>Next →</button>
                : <button className="ab-nav-btn ab-nav-btn-finish" onClick={finishEval}>Results →</button>
              }
            </div>
          </div>

          {/* Pane 3: Challenge interactive UI */}
          <div className="ab-challenge-pane">
            {ChallengeComponent && (
              <ChallengeComponent
                key={activeChallengeId + (results[activeChallengeId] ? '-done' : '-fresh')}
                onComplete={handleChallengeComplete}
              />
            )}
          </div>

        </div>
      )}

      {/* RESULTS */}
      {mode === MODE_RESULTS && (
        <ResultsDashboard
          results={Object.values(results)}
          onReset={resetEval}
          onRetry={(id) => retryChallenge(id)}
          agent={agent}
          evalStartTime={evalStartTime}
          totalTime={timer}
        />
      )}
    </div>
  );
}
